// openEHR RM model — BMM package org.openehr.rm.data_types.uri
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { DATA_VALUE } from "./basic.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * A reference to an object which structurally conforms to the Universal Resource Identifier (URI) RFC-3986 standard. The reference is contained in the \`_value_\` attribute, which is a \`String\`. So-called 'plain-text URIs' that contain RFC-3986 forbidden characters such as spaces etc, are allowed on the basis that they need to be RFC-3986 encoded prior to use in e.g. REST APIs or other contexts relying on machine-level conformance.
 */
export class DV_URI extends DATA_VALUE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.String;

  /**
   * Value of URI as a String. 'Plain-text' URIs are allowed, enabling better readability, but must be RFC-3986 encoded in use.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value.
   * Use this to access openehr_base.String methods.
   */
  get $value(): openehr_base.String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.String wrapper.
   */
  set value(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = openehr_base.String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * A distributed information 'space' in which  information objects  exist. The scheme simultaneously specifies an information space and a mechanism for accessing objects in  that  space.  For  example  if  scheme  = "ftp", it identifies the information space in which  all  ftp-able objects  exist,  and also the application - ftp - which can be used to access them. Values may include: "ftp", "telnet", "mailto", etc. Refer to RFC-3986 for a full list.
   *
   * @returns Result value
   */
  scheme(): openehr_base.String {
    // Extract the scheme from the URI value (everything before the first ':')
    if (!this.value) {
      throw new Error("DV_URI value is required to extract scheme");
    }
    const colonIndex = this.value.indexOf(':');
    if (colonIndex === -1) {
      throw new Error("DV_URI value does not contain a valid scheme (no ':' separator found)");
    }
    return openehr_base.String.from(this.value.substring(0, colonIndex));
  }

  /**
   * A string whose format is  a  function  of  the  scheme. Identifies   the   location  in  <scheme>-space  of  an information entity. Typical values include hierarchical directory  paths  for  any  machine.  For example, with scheme = "ftp", path might be \`"/pub/images/image_01"\`. The strings "." and ".." are reserved for use in the path. Paths may include internet/intranet location identifiers of the form: \`sub_domain...domain\`, e.g. \`"info.cern.ch"\`.
   * @returns Result value
   */
  path(): openehr_base.String {
    // Extract path from URI: scheme://host/path?query#fragment
    if (!this.value) return openehr_base.String.from("");
    
    try {
      const url = new URL(this.value);
      return openehr_base.String.from(url.pathname);
    } catch {
      // Fallback: try to extract path manually
      const colonIdx = this.value.indexOf(':');
      if (colonIdx === -1) return openehr_base.String.from("");
      
      let rest = this.value.substring(colonIdx + 1);
      // Remove leading //
      if (rest.startsWith("//")) rest = rest.substring(2);
      
      // Find path start (after host)
      const pathStart = rest.indexOf('/');
      if (pathStart === -1) return openehr_base.String.from("");
      
      // Remove query and fragment
      let path = rest.substring(pathStart);
      const queryIdx = path.indexOf('?');
      if (queryIdx !== -1) path = path.substring(0, queryIdx);
      const fragIdx = path.indexOf('#');
      if (fragIdx !== -1) path = path.substring(0, fragIdx);
      
      return openehr_base.String.from(path);
    }
  }

  /**
   * A part of, a  fragment  or  a  sub-function  within  an object. Allows references to sub-parts of objects, such as a certain line and character  position  in  a  text object. The  syntax  and semantics are defined by the application responsible for the object.
   *
   * @returns Result value
   */
  fragment_id(): openehr_base.String {
    // Extract fragment from URI (part after #)
    if (!this.value) return openehr_base.String.from("");
    
    const fragIdx = this.value.indexOf('#');
    if (fragIdx === -1) return openehr_base.String.from("");
    
    return openehr_base.String.from(this.value.substring(fragIdx + 1));
  }

  /**
   * Query string to send to application implied  by  scheme and  path.  Enables  queries  to applications, including databases  to  be  included in  the  URI. Supports any query meaningful to the server, including SQL.
   *
   * @returns Result value
   */
  query(): openehr_base.String {
    // Extract query from URI (part after ? and before #)
    if (!this.value) return openehr_base.String.from("");
    
    const queryIdx = this.value.indexOf('?');
    if (queryIdx === -1) return openehr_base.String.from("");
    
    let query = this.value.substring(queryIdx + 1);
    const fragIdx = query.indexOf('#');
    if (fragIdx !== -1) query = query.substring(0, fragIdx);
    
    return openehr_base.String.from(query);
  }
}

/**
 * A \`DV_EHR_URI\` is a \`DV_URI\` which has the scheme name 'ehr', and which can only reference items in EHRs.
 *
 * Used to reference items in an EHR, which may be the same as the current EHR (containing this link), or another.
 */
export class DV_EHR_URI extends DV_URI {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}
